import React, { memo } from 'react';
import { areEqual } from 'Utils/equalityChecks';
import { Modal } from 'Components/Modal';
import { Label } from 'Components/Label';
import { TextBox } from 'Components/TextBox';
import { PurpleButton } from 'Components/Button';

import formClasses from 'Themes/form/form.module.css';
import classes from './nonAppCrewModal.module.css';

interface Props {
  isOpen: boolean;
  isEditMode: boolean;
  firstName: string;
  lastName: string;
  contactEmail: string;
  phoneValue: string;
  phoneType: string;
  laborRate: string;
  canViewCost?: boolean;
  onClose: (e: any) => void;
  onFirstNameChange: (e: any) => void;
  onLastNameChange: (e: any) => void;
  onContactEmailChange: (e: any) => void;
  onPhoneValueChange: (e: any) => void;
  onPhoneTypeChange: (e: any) => void;
  onLaborRateChange: (e: any) => void;
  onSave: (e: any) => void;
  onDeactivate?: (e: any) => void;
}

const NonAppCrewModal = ({
  isOpen,
  isEditMode,
  firstName,
  lastName,
  contactEmail,
  phoneValue,
  phoneType,
  laborRate,
  canViewCost,
  onClose,
  onFirstNameChange,
  onLastNameChange,
  onContactEmailChange,
  onPhoneValueChange,
  onPhoneTypeChange,
  onLaborRateChange,
  onSave,
  onDeactivate,
}: Props) => (
  <Modal
    id="non-app-crew-modal"
    classes={classes}
    title={isEditMode ? 'Edit Non-app Crew' : 'Add Non-app Crew'}
    isOpen={isOpen}
    modalHeader
    leftHeaderIcon="peoplepink"
    dataBsBackdrop="static"
    dataBsKeyboard="false"
    modalCloseClick={onClose}
  >
    <form className={`g-3 ${formClasses.formBase} ${classes.crewForm}`} noValidate action="#">
      <div className={classes.fieldGroup}>
        <Label ariaLabel="First Name" className={formClasses.label} htmlFor="first-name">
          First Name
          {' '}
          <span className="text-danger">*</span>
        </Label>
        <TextBox
          type="text"
          name="first-name"
          value={firstName}
          onChange={onFirstNameChange}
          placeholder="First name"
          ariaLabel="First name"
        />
      </div>

      <div className={classes.fieldGroup}>
        <Label ariaLabel="Last Name" className={formClasses.label} htmlFor="last-name">
          Last Name
        </Label>
        <TextBox
          type="text"
          name="last-name"
          value={lastName}
          onChange={onLastNameChange}
          placeholder="Last name"
          ariaLabel="Last name"
        />
      </div>

      <div className={classes.fieldGroup}>
        <Label ariaLabel="Contact Email" className={formClasses.label} htmlFor="contact-email">
          Contact Email
        </Label>
        <TextBox
          type="email"
          name="contact-email"
          value={contactEmail}
          onChange={onContactEmailChange}
          placeholder="email@example.com"
          ariaLabel="Contact email"
        />
      </div>

      <div className={classes.fieldGroup}>
        <Label ariaLabel="Phone Number" className={formClasses.label} htmlFor="phone-value">
          Phone Number
        </Label>
        <div className="d-flex gap-2">
          <TextBox
            type="text"
            name="phone-value"
            value={phoneValue}
            onChange={onPhoneValueChange}
            placeholder="Phone number"
            ariaLabel="Phone number"
            className="flex-grow-1"
          />
          <select
            name="phone-type"
            value={phoneType}
            onChange={onPhoneTypeChange}
            className={classes.phoneTypeSelect}
            aria-label="Phone type"
          >
            <option value="mobile">Mobile</option>
            <option value="work">Work</option>
            <option value="home">Home</option>
            <option value="fax">Fax</option>
          </select>
        </div>
      </div>

      {canViewCost && (
        <div className={classes.fieldGroup}>
          <Label ariaLabel="Labor Rate" className={formClasses.label} htmlFor="labor-rate">
            Labor Rate ($/hr)
          </Label>
          <TextBox
            type="number"
            name="labor-rate"
            value={laborRate}
            onChange={onLaborRateChange}
            placeholder="Hourly rate (leave blank to inherit)"
            ariaLabel="Labor rate per hour"
          />
        </div>
      )}

      <div className={`modal-footer ${classes.modalFooter}`}>
        {isEditMode && onDeactivate && (
          <PurpleButton onClick={onDeactivate} className={classes.deactivateButton}>
            Deactivate
          </PurpleButton>
        )}
        <PurpleButton onClick={onClose} className={classes.cancelButton}>
          Cancel
        </PurpleButton>
        <PurpleButton
          onClick={onSave}
          disabled={!firstName?.trim()}
          className={classes.saveButton}
        >
          {isEditMode ? 'Save' : 'Add'}
        </PurpleButton>
      </div>
    </form>
  </Modal>
);

NonAppCrewModal.defaultProps = {
  canViewCost: false,
  onDeactivate: undefined,
};

const NonAppCrewModalMemo = memo(NonAppCrewModal, areEqual);

export { NonAppCrewModalMemo as NonAppCrewModal };
